import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminService } from '../../services/adminService';
import {
  Search, Plus, Edit, Trash2, Download, X, Users as UsersIcon, Save, BadgeCheck, ShieldCheck,
  Image as ImageIcon, KeyRound, Phone, Mail, RefreshCw, UserCheck, Package,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  formatAppVersionBadge, formatAppPlatform, formatVersionReportedAt, LATEST_STAFF_APP_VERSION,
} from '../../constants/appRelease';

const PAGE_SIZE = 20;

const ROLE_BADGE = {
  admin: 'bg-red-50 text-red-700 ring-red-600/15',
  mechanic: 'bg-violet-50 text-violet-700 ring-violet-600/15',
  telecaller: 'bg-orange-50 text-orange-700 ring-orange-600/15',
  teamlead: 'bg-cyan-50 text-cyan-700 ring-cyan-600/15',
  supervisor: 'bg-pink-50 text-pink-700 ring-pink-600/15',
  partner: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
  serviceexecutive: 'bg-amber-50 text-amber-700 ring-amber-600/15',
  serviceadvisor: 'bg-purple-50 text-purple-700 ring-purple-600/15',
  customer: 'bg-slate-100 text-slate-700 ring-slate-600/15',
  marketing: 'bg-blue-50 text-blue-700 ring-blue-600/15',
  accounts: 'bg-teal-50 text-teal-700 ring-teal-600/15',
  tlmanager: 'bg-indigo-50 text-indigo-700 ring-indigo-600/15',
};

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  inactive: 'bg-red-50 text-red-700 ring-red-600/20',
  suspended: 'bg-amber-50 text-amber-700 ring-amber-600/20',
};

const StatPill = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate: 'text-slate-700',
    primary: 'text-primary-700',
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
  };
  return (
    <span className="text-xs text-slate-500">
      {label}{' '}
      <span className={`font-semibold ${tones[tone] || tones.slate}`}>{value}</span>
    </span>
  );
};

const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
      ROLE_BADGE[role?.toLowerCase()] || 'bg-slate-100 text-slate-700 ring-slate-600/15'
    }`}
  >
    {role}
  </span>
);

const StatusBadge = ({ status }) => {
  const key = (status || 'active').toLowerCase();
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
        STATUS_BADGE[key] || STATUS_BADGE.active
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${key === 'active' ? 'bg-emerald-500' : key === 'inactive' ? 'bg-red-500' : 'bg-amber-500'}`} />
      {status || 'active'}
    </span>
  );
};

const MEMBERSHIP_STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  expired: 'bg-red-50 text-red-700 ring-red-600/20',
  cancelled: 'bg-slate-100 text-slate-600 ring-slate-600/15',
};

const CustomerPlanBadge = ({ planName, membershipStatus }) => {
  const statusKey = (membershipStatus || '').toLowerCase();
  const statusClass = MEMBERSHIP_STATUS_BADGE[statusKey] || 'bg-indigo-50 text-indigo-700 ring-indigo-600/20';

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-600/15">
        <Package size={11} className="shrink-0" />
        {planName || 'No plan'}
      </span>
      {membershipStatus && (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ring-1 ring-inset ${statusClass}`}>
          {membershipStatus}
        </span>
      )}
    </div>
  );
};

const UserAvatar = ({ name, avatarUrl, size = 'md' }) => {
  const [imgError, setImgError] = useState(false);
  const initial = (name || 'U').charAt(0).toUpperCase();
  const sizeClass = size === 'sm' ? 'h-10 w-10 text-sm' : 'h-11 w-11 text-sm';
  const hasPhoto = Boolean(avatarUrl?.trim()) && !imgError;

  if (hasPhoto) {
    return (
      <img
        src={avatarUrl.trim()}
        alt={name || 'User'}
        onError={() => setImgError(true)}
        className={`${sizeClass} shrink-0 rounded-full object-cover ring-2 ring-white`}
      />
    );
  }

  return (
    <div className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-indigo-100 font-bold text-primary-700 ring-2 ring-white`}>
      {initial}
    </div>
  );
};

const IconAction = ({ icon: Icon, label, onClick, tone = 'neutral' }) => {
  const tones = {
    neutral: 'text-slate-500 hover:bg-white hover:text-slate-800',
    edit: 'text-primary-600 hover:bg-primary-50',
    team: 'text-emerald-600 hover:bg-emerald-50',
    password: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`rounded-lg p-2 transition-colors ${tones[tone]}`}
    >
      <Icon size={16} />
    </button>
  );
};

const fieldClass =
  'w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-900 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20';
const labelClass = 'mb-2 block text-sm font-semibold text-slate-700';

const ModalShell = ({ title, subtitle, icon: Icon, iconBg, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
    <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
              <Icon size={20} />
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={20} />
        </button>
      </div>
      <div className="max-h-[calc(90vh-140px)] overflow-y-auto px-6 py-5">{children}</div>
      {footer && <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4">{footer}</div>}
    </div>
  </div>
);

const Users = () => {
  const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const isAdmin = Boolean(
    currentUser?.currentRole === 'admin' ||
    currentUser?.role === 'admin' ||
    (Array.isArray(currentUser?.roles) && currentUser.roles.includes('admin'))
  );
  const [activeTab, setActiveTab] = useState('users'); // users | kyc
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [appVersionFilter, setAppVersionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTLChangeModal, setShowTLChangeModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForTLChange, setSelectedUserForTLChange] = useState(null);
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    roles: [],
    status: 'active',
    teamLeaderId: '',
    serviceAdvisorId: '',
    dialerId: '',
    customerType: 'unregistered'
  });

  const [editingUser, setEditingUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [changingTL, setChangingTL] = useState(false);
  const [newTeamLeadId, setNewTeamLeadId] = useState('');

  const availableRoles = [
    'admin', 'mechanic', 'telecaller', 'teamlead', 'supervisor', 'partner',
    'serviceexecutive', 'serviceadvisor', 'customer', 'marketing', 'accounts', 'tlmanager'
  ];

  const { data: usersData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['adminUsers', debouncedSearch, roleFilter, statusFilter, appVersionFilter, currentPage],
    queryFn: () => AdminService.listUsers({
      search: debouncedSearch,
      role: roleFilter,
      status: statusFilter,
      appVersion: appVersionFilter,
      page: currentPage,
      pageSize: PAGE_SIZE,
    }),
    placeholderData: (previousData) => previousData,
  });

  const users = usersData?.items || [];
  const userStats = usersData?.stats || {
    total_users: 0,
    active_users: 0,
    inactive_users: 0,
    filtered_total: 0,
  };
  const availableAppVersions = usersData?.appVersions || [];
  const filteredTotal = Number(usersData?.total || 0);
  const totalPages = Math.max(Number(usersData?.totalPages || 1), 1);
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const rangeStart = filteredTotal === 0 ? 0 : (safeCurrentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safeCurrentPage * PAGE_SIZE, filteredTotal);

  const { data: teamLeaders = [] } = useQuery({
    queryKey: ['adminTeamLeads'],
    queryFn: async () => {
      const leads = await AdminService.getTeamLeads({ status: 'active' });
      return leads.map((u) => ({ id: u.id, name: u.name, email: u.email }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: kycRows = [], isLoading: isKycLoading, refetch: refetchKyc } = useQuery({
    queryKey: ['adminTelecallerKyc'],
    queryFn: AdminService.getTelecallerKyc,
    enabled: isAdmin && activeTab === 'kyc',
  });

  // Derived lists: keep them memoized (avoid setState loop)
  const serviceAdvisors = useMemo(() => {
    return (users || [])
      .filter((u) => u.roles?.includes('serviceadvisor'))
      .map((u) => ({ id: u.id, name: u.name, email: u.email }));
  }, [users]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, roleFilter, statusFilter, appVersionFilter]);

  const handleExport = async () => {
    const exportResult = await AdminService.listUsers({
      search: debouncedSearch,
      role: roleFilter,
      status: statusFilter,
      appVersion: appVersionFilter,
      page: 1,
      pageSize: Math.min(Math.max(filteredTotal, 1), 5000),
    });
    const exportRows = exportResult.items || [];
    const headers = ['Name', 'Email', 'Phone', 'Roles', 'Plan', 'Membership Status', 'Status', 'App Version', 'Platform', 'Created'];
    const rows = exportRows.map(u => [
      u.name || '',
      u.email || '',
      u.phone || '',
      (u.roles || []).join('; '),
      u.roles?.some((r) => r?.toLowerCase() === 'customer') ? (u.plan_name || '') : '',
      u.roles?.some((r) => r?.toLowerCase() === 'customer') ? (u.membership_status || '') : '',
      u.status || 'active',
      u.app_version ? `${u.app_version}${u.app_build_number ? ` (${u.app_build_number})` : ''}` : '',
      formatAppPlatform(u.app_platform)?.label || u.app_platform || '',
      u.created_at ? new Date(u.created_at).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `roadserve-users-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const [kycSearch, setKycSearch] = useState('');
  const [kycStatusFilter, setKycStatusFilter] = useState('all'); // all | verified | pending | missing
  const [savingKyc, setSavingKyc] = useState({});

  const filteredKyc = useMemo(() => {
    const q = kycSearch.trim().toLowerCase();
    return (kycRows || []).filter((row) => {
      const matchesSearch =
        !q ||
        (row.name || '').toLowerCase().includes(q) ||
        (row.email || '').toLowerCase().includes(q) ||
        (row.phone || '').includes(q);

      const hasAadhaar = Boolean(row.aadhaar_front_image_path) && Boolean(row.aadhaar_back_image_path);
      const isVerified = Boolean(row.is_verified);
      const status =
        !hasAadhaar ? 'missing'
        : isVerified ? 'verified'
        : 'pending';

      const matchesStatus = kycStatusFilter === 'all' || status === kycStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [kycRows, kycSearch, kycStatusFilter]);

  const openDoc = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSaveKyc = async (row, next) => {
    setSavingKyc((prev) => ({ ...prev, [row.user_id]: true }));
    try {
      const res = await AdminService.updateTelecallerKyc(row.user_id, next);
      if (!res.success) {
        alert(res.error || 'Failed to update KYC');
        return;
      }
      await refetchKyc();
    } finally {
      setSavingKyc((prev) => ({ ...prev, [row.user_id]: false }));
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      alert('Please fill in all required fields');
      return;
    }

    if (newUser.roles.length === 0) {
      alert('Please select at least one role');
      return;
    }

    if (newUser.roles.includes('telecaller') && !newUser.teamLeaderId) {
      alert('Please assign a team leader for telecaller role');
      return;
    }

    setCreatingUser(true);
    try {
      const result = await AdminService.createUser(newUser);
      if (result.success) {
        alert('User created successfully!');
        setShowCreateModal(false);
        setNewUser({
          name: '', email: '', phone: '', password: '',
          roles: [], status: 'active', teamLeaderId: '',
          serviceAdvisorId: '', dialerId: '', customerType: 'unregistered'
        });
        refetch();
      } else {
        alert(result.error || 'Failed to create user');
      }
    } catch (error) {
      alert('Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser || editingUser.roles.length === 0) {
      alert('Please select at least one role');
      return;
    }

    setUpdatingUser(true);
    try {
      const result = await AdminService.updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        status: editingUser.status,
        roles: editingUser.roles,
        dialer_user_id: editingUser.dialerId || null
      });
      if (result.success) {
        alert('User updated successfully!');
        setShowEditModal(false);
        setEditingUser(null);
        refetch();
      } else {
        alert(result.error || 'Failed to update user');
      }
    } catch (error) {
      alert('Failed to update user');
    } finally {
      setUpdatingUser(false);
    }
  };

  const openPasswordResetModal = (user) => {
    setSelectedUserForPasswordReset(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordResetModal(true);
  };

  const closePasswordResetModal = () => {
    setShowPasswordResetModal(false);
    setSelectedUserForPasswordReset(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleResetPassword = async () => {
    if (!selectedUserForPasswordReset?.email) {
      alert('User email is missing');
      return;
    }
    if (!newPassword || !confirmPassword) {
      alert('Please enter and confirm the new password');
      return;
    }
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setResettingPassword(true);
    try {
      const result = await AdminService.resetUserPassword(
        selectedUserForPasswordReset.email,
        newPassword
      );
      if (result.success) {
        alert(result.message || 'Password reset successfully');
        closePasswordResetModal();
      } else {
        alert(result.error || 'Failed to reset password');
      }
    } catch (error) {
      alert('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const result = await AdminService.deleteUser(selectedUser.id);
      if (result.success) {
        alert('User deleted successfully');
        setShowDeleteConfirm(false);
        setSelectedUser(null);
        refetch();
      } else {
        alert(result.error || 'Failed to delete user');
      }
    } catch (error) {
      alert('Failed to delete user');
    }
  };

  const handleChangeTeamLead = async () => {
    if (!selectedUserForTLChange || !newTeamLeadId) {
      alert('Please select both a user and a new team lead');
      return;
    }

    setChangingTL(true);
    try {
      const result = await AdminService.changeTeamLead(
        selectedUserForTLChange.id,
        newTeamLeadId,
        currentUser?.id || null // changedBy (uuid)
      );
      if (result.success) {
        alert(`Team Lead changed successfully for ${selectedUserForTLChange.name}`);
        setShowTLChangeModal(false);
        setSelectedUserForTLChange(null);
        setNewTeamLeadId('');
        refetch();
      } else {
        alert(result.error || 'Failed to change team lead');
      }
    } catch (error) {
      alert('Failed to change team lead');
    } finally {
      setChangingTL(false);
    }
  };

  const toggleRole = (role, isEdit = false) => {
    if (isEdit && editingUser) {
      setEditingUser({
        ...editingUser,
        roles: editingUser.roles.includes(role)
          ? editingUser.roles.filter(r => r !== role)
          : [...editingUser.roles, role]
      });
    } else {
      setNewUser({
        ...newUser,
        roles: newUser.roles.includes(role)
          ? newUser.roles.filter(r => r !== role)
          : [...newUser.roles, role]
      });
    }
  };

  const getRoleBadgeClass = (role) => ROLE_BADGE[role?.toLowerCase()] || ROLE_BADGE.customer;

  if (isLoading) {
    return (
      <div className="flex h-[28rem] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
          <p className="text-sm font-medium text-slate-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Compact header */}
      <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900 sm:text-xl">User Management</h1>
            {activeTab === 'users' && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <StatPill label="Showing" value={filteredTotal} tone="primary" />
                <StatPill label="Total Users" value={userStats.total_users} />
                <StatPill label="Active" value={userStats.active_users} tone="emerald" />
                <StatPill label="Inactive" value={userStats.inactive_users} tone="rose" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Download size={14} />
              Export
            </button>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-700"
            >
              <Plus size={14} />
              Add User
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1 px-3 py-1.5">
          <button
            type="button"
            onClick={() => setActiveTab('users')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              activeTab === 'users'
                ? 'bg-primary-50 text-primary-700'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            Users
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setActiveTab('kyc')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                activeTab === 'kyc'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <ShieldCheck size={14} />
              Telecaller KYC
            </button>
          )}
        </div>
      </div>

      {/* Users list */}
      {activeTab === 'users' && (
        <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-3 py-3 lg:px-4">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search by name, email or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50/50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
                <select
                  value={appVersionFilter}
                  onChange={(e) => setAppVersionFilter(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="all">All App Versions</option>
                  <option value="latest">Latest ({LATEST_STAFF_APP_VERSION})</option>
                  <option value="outdated">Outdated</option>
                  <option value="not_reported">Not Reported</option>
                  {availableAppVersions.map((version) => (
                    <option key={version} value={version}>
                      {version}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setRoleFilter('all')}
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold transition ${
                  roleFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All
              </button>
              {['telecaller', 'teamlead', 'customer', 'mechanic', 'admin'].map(role => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setRoleFilter(role)}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize transition ring-1 ring-inset ${
                    roleFilter === role
                      ? `${getRoleBadgeClass(role)} ring-transparent`
                      : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80">
                  <th className="min-w-[240px] px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">User</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Roles</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">App Version</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Created</th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.id} className="group transition hover:bg-slate-50/70">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <UserAvatar name={user.name} avatarUrl={user.avatar_url} />
                        <div className="min-w-0 space-y-0.5">
                          <p className="truncate text-sm font-semibold text-slate-900">{user.name || 'N/A'}</p>
                          {user.email && (
                            <p className="flex items-center gap-1.5 truncate text-xs text-slate-500">
                              <Mail size={12} className="shrink-0 text-slate-400" />
                              <span className="truncate">{user.email}</span>
                            </p>
                          )}
                          <p className="flex items-center gap-1.5 text-xs text-slate-600">
                            <Phone size={12} className="shrink-0 text-slate-400" />
                            <span className="font-medium tabular-nums">{user.phone || '—'}</span>
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div>
                        <div className="flex flex-wrap gap-1.5">
                          {user.roles?.length ? user.roles.map((role, idx) => (
                            <RoleBadge key={idx} role={role} />
                          )) : (
                            <span className="text-xs text-slate-400">No role</span>
                          )}
                        </div>
                        {user.roles?.some((r) => r?.toLowerCase() === 'customer') && (
                          <CustomerPlanBadge
                            planName={user.plan_name}
                            membershipStatus={user.membership_status}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const badge = formatAppVersionBadge(user);
                        const platformBadge = formatAppPlatform(user.app_platform);
                        return (
                          <div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className={`inline-flex rounded-lg px-2.5 py-1 text-xs font-semibold ${badge.className}`}>
                                {badge.label}
                              </span>
                              {platformBadge && (
                                <span className={`inline-flex rounded-lg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${platformBadge.className}`}>
                                  {platformBadge.label}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-[11px] text-slate-400">
                              {user.app_version_reported_at
                                ? `Seen ${formatVersionReportedAt(user.app_version_reported_at)}`
                                : 'Never opened on mobile'}
                            </p>
                            {!badge.isLatest && user.app_version && (
                              <p className="mt-0.5 text-[11px] font-medium text-amber-700">
                                Latest: {LATEST_STAFF_APP_VERSION}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN') : 'N/A'}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end">
                        <div className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-50/80 p-1 opacity-90 transition group-hover:opacity-100">
                          <IconAction
                            icon={Edit}
                            label="Edit user"
                            tone="edit"
                            onClick={() => {
                              setEditingUser({ ...user, dialerId: user.dialer_user_id || '' });
                              setShowEditModal(true);
                            }}
                          />
                          {(user.roles?.includes('telecaller') || user.roles?.includes('serviceexecutive')) && (
                            <IconAction
                              icon={UserCheck}
                              label="Change team lead"
                              tone="team"
                              onClick={() => {
                                setSelectedUserForTLChange(user);
                                setNewTeamLeadId('');
                                setShowTLChangeModal(true);
                              }}
                            />
                          )}
                          <IconAction
                            icon={KeyRound}
                            label="Reset password"
                            tone="password"
                            onClick={() => openPasswordResetModal(user)}
                          />
                          <IconAction
                            icon={Trash2}
                            label="Deactivate user"
                            tone="danger"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteConfirm(true);
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTotal === 0 && !isLoading && (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
                <UsersIcon size={28} className="text-slate-400" />
              </div>
              <p className="text-base font-semibold text-slate-700">No users found</p>
              <p className="mt-1 text-sm text-slate-500">Try changing search or filter criteria</p>
            </div>
          )}

          {filteredTotal > 0 && (
            <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-5">
              <p className="text-xs text-slate-500">
                Showing {rangeStart}–{rangeEnd} of {filteredTotal}
                {isFetching && !isLoading ? ' · Updating...' : ''}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={safeCurrentPage <= 1}
                  className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>
                <span className="min-w-[88px] text-center text-xs font-medium text-slate-600">
                  Page {safeCurrentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={safeCurrentPage >= totalPages}
                  className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KYC Tab */}
      {activeTab === 'kyc' && isAdmin && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4 lg:p-5">
            <div className="mb-1 flex items-center gap-2">
              <ShieldCheck size={18} className="text-primary-600" />
              <h2 className="text-lg font-bold text-slate-900">Telecaller KYC</h2>
            </div>
            <p className="mb-4 text-sm text-slate-500">Review documents, verify identity and set commission</p>
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Search telecallers..."
                  value={kycSearch}
                  onChange={(e) => setKycSearch(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-2.5 pl-10 pr-4 text-sm focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
              <select
                value={kycStatusFilter}
                onChange={(e) => setKycStatusFilter(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="missing">Missing Docs</option>
              </select>
            </div>
          </div>

          {isKycLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Telecaller</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">KYC</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Docs</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Commission %</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Verified</th>
                    <th className="px-5 py-3.5 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredKyc.map((row) => {
                    const hasAadhaar = Boolean(row.aadhaar_front_image_path) && Boolean(row.aadhaar_back_image_path);
                    const status =
                      !hasAadhaar ? { label: 'Missing', color: 'bg-red-50 text-red-700 ring-red-600/15' }
                      : row.is_verified ? { label: 'Verified', color: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15' }
                      : { label: 'Pending', color: 'bg-amber-50 text-amber-700 ring-amber-600/15' };

                    return (
                      <tr key={row.user_id} className="transition hover:bg-slate-50/70">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-indigo-100 text-sm font-bold text-primary-700">
                              {(row.name || 'T').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{row.name || 'N/A'}</p>
                              <p className="text-xs text-slate-500">{row.email || 'N/A'}</p>
                              <p className="text-xs text-slate-400">{row.phone || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${status.color}`}>
                            {status.label}
                          </span>
                          <p className="mt-1 text-[11px] text-slate-400">
                            {row.kyc_updated_at ? new Date(row.kyc_updated_at).toLocaleString('en-IN') : ''}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-1.5">
                            <button
                              type="button"
                              onClick={() => openDoc(row.aadhaar_front_image_path)}
                              disabled={!row.aadhaar_front_image_path}
                              className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                                row.aadhaar_front_image_path ? 'text-primary-600 hover:text-primary-800' : 'text-slate-300'
                              }`}
                            >
                              <ImageIcon size={14} /> Aadhaar Front
                            </button>
                            <button
                              type="button"
                              onClick={() => openDoc(row.aadhaar_back_image_path)}
                              disabled={!row.aadhaar_back_image_path}
                              className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                                row.aadhaar_back_image_path ? 'text-primary-600 hover:text-primary-800' : 'text-slate-300'
                              }`}
                            >
                              <ImageIcon size={14} /> Aadhaar Back
                            </button>
                            <button
                              type="button"
                              onClick={() => openDoc(row.pan_image_path)}
                              disabled={!row.pan_image_path}
                              className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                                row.pan_image_path ? 'text-primary-600 hover:text-primary-800' : 'text-slate-300'
                              }`}
                            >
                              <ImageIcon size={14} /> PAN (optional)
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            defaultValue={row.commission_percent ?? ''}
                            onChange={(e) => {
                              row.__commissionDraft = e.target.value;
                            }}
                            className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                            placeholder="e.g. 5"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <input
                              type="checkbox"
                              defaultChecked={Boolean(row.is_verified)}
                              onChange={(e) => {
                                row.__verifiedDraft = e.target.checked;
                              }}
                              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-slate-700">Verified</span>
                          </label>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              handleSaveKyc(row, {
                                is_verified: typeof row.__verifiedDraft === 'boolean' ? row.__verifiedDraft : Boolean(row.is_verified),
                                commission_percent: row.__commissionDraft ?? row.commission_percent ?? null,
                              })
                            }
                            disabled={savingKyc[row.user_id]}
                            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50"
                          >
                            {savingKyc[row.user_id] ? (
                              <>
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <ShieldCheck size={16} /> Save
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredKyc.length === 0 && (
                <div className="px-6 py-16 text-center">
                  <BadgeCheck size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="font-semibold text-slate-700">No telecaller KYC records found</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <ModalShell
          title="Create New User"
          subtitle="Add a team member with role and login credentials"
          icon={Plus}
          iconBg="bg-primary-50 text-primary-600"
          onClose={() => setShowCreateModal(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateUser}
                disabled={creatingUser}
                className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {creatingUser ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Create User
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Name *</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className={fieldClass}
                placeholder="Enter full name"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className={fieldClass}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className={fieldClass}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Password *</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className={fieldClass}
                placeholder="Enter password"
              />
            </div>
            <div>
              <label className={labelClass}>Roles *</label>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {availableRoles.map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRole(role)}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium capitalize transition ${
                      newUser.roles.includes(role)
                        ? 'border-primary-500 bg-primary-600 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
              {newUser.roles.length === 0 && (
                <p className="mt-2 text-sm text-red-500">Please select at least one role</p>
              )}
            </div>
            {newUser.roles.includes('telecaller') && (
              <div>
                <label className={labelClass}>Team Leader *</label>
                <select
                  value={newUser.teamLeaderId}
                  onChange={(e) => setNewUser({ ...newUser, teamLeaderId: e.target.value })}
                  className={fieldClass}
                >
                  <option value="">Select Team Leader</option>
                  {teamLeaders.map(tl => (
                    <option key={tl.id} value={tl.id}>{tl.name}</option>
                  ))}
                </select>
              </div>
            )}
            {newUser.roles.includes('customer') && (
              <div>
                <label className={labelClass}>Customer Type</label>
                <div className="flex gap-2">
                  {['unregistered', 'registered'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setNewUser({ ...newUser, customerType: type })}
                      className={`flex-1 rounded-xl border px-4 py-2 capitalize ${
                        newUser.customerType === type
                          ? 'border-primary-500 bg-primary-600 text-white'
                          : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <ModalShell
          title="Edit User"
          subtitle={editingUser.email || 'Update profile and access'}
          icon={Edit}
          iconBg="bg-indigo-50 text-indigo-600"
          onClose={() => setShowEditModal(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEditUser}
                disabled={updatingUser}
                className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {updatingUser ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  className={fieldClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={editingUser.email || ''}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={editingUser.status || 'active'}
                onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                className={fieldClass}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Roles *</label>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {availableRoles.map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRole(role, true)}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium capitalize transition ${
                      editingUser.roles?.includes(role)
                        ? 'border-primary-500 bg-primary-600 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <ModalShell
          title="Deactivate User"
          subtitle={`${selectedUser.name} will be marked inactive and won't be able to login.`}
          icon={Trash2}
          iconBg="bg-red-50 text-red-600"
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedUser(null);
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                }}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
              >
                Deactivate
              </button>
            </>
          }
        >
          <p className="text-sm text-slate-600">
            Are you sure you want to deactivate <strong>{selectedUser.name}</strong>?
          </p>
        </ModalShell>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && selectedUserForPasswordReset && (
        <ModalShell
          title="Reset Password"
          subtitle="New password will work immediately on staff app login"
          icon={KeyRound}
          iconBg="bg-amber-50 text-amber-600"
          onClose={closePasswordResetModal}
          footer={
            <>
              <button
                type="button"
                onClick={closePasswordResetModal}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
              >
                {resettingPassword ? 'Resetting...' : 'Reset Password'}
              </button>
            </>
          }
        >
          <p className="mb-4 text-sm text-slate-600">
            Set a new password for <strong>{selectedUserForPasswordReset.name}</strong>
            {selectedUserForPasswordReset.email ? ` (${selectedUserForPasswordReset.email})` : ''}
          </p>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>New Password *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={fieldClass}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className={labelClass}>Confirm Password *</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={fieldClass}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </div>
          </div>
        </ModalShell>
      )}

      {/* Team Lead Change Modal */}
      {showTLChangeModal && selectedUserForTLChange && (
        <ModalShell
          title="Change Team Lead"
          subtitle={`Reassign ${selectedUserForTLChange.name} to a different team lead`}
          icon={UserCheck}
          iconBg="bg-emerald-50 text-emerald-600"
          onClose={() => setShowTLChangeModal(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowTLChangeModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleChangeTeamLead}
                disabled={changingTL || !newTeamLeadId}
                className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {changingTL ? 'Changing...' : 'Change Team Lead'}
              </button>
            </>
          }
        >
          <label className={labelClass}>New Team Lead</label>
          <select
            value={newTeamLeadId}
            onChange={(e) => setNewTeamLeadId(e.target.value)}
            className={fieldClass}
          >
            <option value="">Select Team Lead</option>
            {teamLeaders.map(tl => (
              <option key={tl.id} value={tl.id}>{tl.name}</option>
            ))}
          </select>
        </ModalShell>
      )}
    </div>
  );
};

export default Users;
